import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { faFileExcel, faPrint, faQuestionCircle, faSpinner, faUserGraduate } from '@fortawesome/free-solid-svg-icons';
import { School } from '../../core/models/school.model';
import { District } from '../../core/models/district.model';
import { Student } from '../../core/models/student.model';
import { SchoolClass } from '../../core/models/school-class.model';
import { SessionStorageService } from '../../core/services/session-storage.service';
import { ManagementUser } from '../../core/models/management-user.model';
import { PrintService } from 'src/app/core/services/print.service';
import { ReportsService } from 'src/app/core/services/reports.service';
import { LoadingService } from '../../core/services/loading.service';
import * as Sentry from '@sentry/angular';
import { reportsStudentsListIntroSteps } from './reports-students-list.intro-steps';
import { CATEGORY_ICONS } from 'src/app/shared/standards/icons';
import { IntroStep } from '../../core/services/tour.service';
import { canChangeSchool } from '../../reports/utils';

@Component({
    selector: 'wf-reports-students-list',
    templateUrl: './reports-students-list.component.html',
    styleUrls: ['./reports-students-list.component.css'],
    standalone: false
})
export class ReportsStudentsListComponent implements OnInit {
  @ViewChild('printContent') printContent!: ElementRef<HTMLElement>;

  managementUser: ManagementUser | null = null;
  isParentUser: boolean = false;
  userRole: string = '';

  students: Student[] = [];
  filteredStudents: Student[] = [];
  grades: string[] = [];
  classes: SchoolClass[] = [];
  schoolFilterError: string = '';

  showSchoolFilter: boolean = true;
  districts: District[] = [];
  schools: School[] = [];
  teachers: ManagementUser[] = [];

  searchTerm: string = '';
  exportingReport: boolean = false;
  exportError: string = '';

  reportsIcon = CATEGORY_ICONS['school-district-reports'];
  excelIcon = faFileExcel;
  printIcon = faPrint;
  loadingIcon = faSpinner;
  studentIcon = faUserGraduate;
  public Math = Math;
  helpIcon = faQuestionCircle;

  tourSteps: IntroStep[] = [];

  constructor(
    private route: ActivatedRoute,
    private reportService: ReportsService,
    private sessionService: SessionStorageService,
    private loadingService: LoadingService,
    private printService: PrintService
  ) {}

  ngOnInit(): void {
    this.managementUser = this.sessionService.userData();
    if (this.managementUser) {
      this.isParentUser = this.managementUser.isParentUser();
      this.userRole = this.managementUser.userRole;
      this.tourSteps = reportsStudentsListIntroSteps(canChangeSchool(this.managementUser));
    }

    const filterData = this.route.snapshot.data['filterData'];
    if (filterData) {
      this.districts = filterData.districts;
      this.schools = filterData.schools;
      this.teachers = filterData.teachers;
    }

    this.schools.unshift({ schoolID: 0, districtID: 0, name: 'All', enabled: true });
    this.teachers.unshift(ManagementUser.getGenericUser());

    this.updateStudentListFilter();
  }

  /**
   * Filters the list of students by first or last name based on the searchTerm.
   */
  searchStudents(): void {
    if (!this.students) {
      return;
    }

    const term = this.searchTerm.toLowerCase();
    if (!term) {
      this.filteredStudents = [...this.students];
    } else {
      this.filteredStudents = this.students.filter(student =>
        student.firstName.toLowerCase().includes(term) ||
        student.lastName.toLowerCase().includes(term)
      );
    }
  }

  /**
   * Updates the student list based on the selected school and teacher filter.
   * Fetches updated student data from the server and reapplies any active search.
   */
  updateStudentListFilter(): void {
    this.loadingService.start() ;
    this.reportService.getStudentListReportData().subscribe({
      next: (data: any) => {
        this.loadingService.finish() ;
        if (data.erred) {
          this.schoolFilterError = data.message;
          this.students = [];
          this.filteredStudents = [];
          this.grades = [];
          this.classes = [];
        } else {
          this.schoolFilterError = '';
          this.students = data || [];
          this.searchStudents();
        }
      },
      error: (err: any) => {
        this.loadingService.finish() ;
        console.error('Error fetching student list data:', err);
        this.schoolFilterError = 'An error occurred while fetching data.';

        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'students-list',
            action: 'get-student-list-data',
          }
        }) ;
      }
    });
  }

  /**
   * Opens the print window to print the student list.
   */
  printStudents(): void {
    const school = this.reportService.getSelectedSchoolForReports()?.name;
    const teacher = this.reportService.getSelectedTeacherForReports()?.firstName + ' ' + this.reportService.getSelectedTeacherForReports()?.lastName;

    this.printService.openPrintWindow(this.printContent.nativeElement, (school ? school.trim() : ''), (teacher ? teacher?.trim() : ''));
  }

  exportStudentList(): void {
    this.exportError = '';
    this.exportingReport = true;
    this.reportService.downloadStudentListReport(this.filteredStudents.map(student => student.userID)).subscribe({
      next: (blob: Blob) => {
        const a = document.createElement('a');
        const objectUrl = URL.createObjectURL(blob);
        a.href = objectUrl;
        a.download = 'student_list.xlsx';
        a.click();
        URL.revokeObjectURL(objectUrl);
        this.exportingReport = false;
      },
      error: (err: any) => {
        this.exportingReport = false;
        this.exportError = 'There was an error trying to export the Student List. Please contact support for assistance.';

        Sentry.captureException(err, {
          tags: {
            section: 'reports',
            report: 'students-list',
            action: 'export',
          }
        });
      }
    });
  }
}
